import { Trash2, Eye, X, Book, BookA } from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { useState } from "react";
import { deleteEventRegistration } from "../../../services/sapEventServices";
import DeleteConfirmationModal from "../members/DeleteMemberModal";

// View Details Modal Component
const ViewDetailsModal = ({ isOpen, onClose, eventData }) => {
  if (!isOpen || !eventData) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-[#4F22D8] text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Book />
            <span className="text-xl font-semibold text-white">
              Registration Details
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            title="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-600">
                Name
              </label>
              <p className="text-base text-gray-900">
                {eventData.name || "N/A"}
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-600">
                Mobile
              </label>
              <p className="text-base text-gray-900">
                {eventData.mobile || "N/A"}
              </p>
            </div>

            <div className="space-y-1 md:col-span-2">
              <label className="text-sm font-semibold text-gray-600">
                Email
              </label>
              <p className="text-base text-gray-900 break-all">
                {eventData.email || "N/A"}
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-600">
                Location
              </label>
              <p className="text-base text-gray-900">
                {eventData.location || "N/A"}
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-600">
                Company Name
              </label>
              <p className="text-base text-gray-900">
                {eventData.companyName || "N/A"}
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-600">
                Designation
              </label>
              <p className="text-base text-gray-900">
                {eventData.designation || "N/A"}
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-600">
                Created At
              </label>
              <p className="text-base text-gray-900">
                {eventData.createdAt
                  ? format(new Date(eventData.createdAt), "PPpp")
                  : "N/A"}
              </p>
            </div>

            <div className="space-y-1 md:col-span-2">
              <label className="text-sm font-semibold text-gray-600">
                Job Description
              </label>
              <p className="text-base text-gray-900 whitespace-pre-wrap">
                {eventData.jobDescription || "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[#4F22D8] text-white rounded-lg hover:bg-[#3d1aa8] transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const SapWasEventListTable = ({
  events = [],
  refetch,
  currentIndex = 0,
  entriesPerPage = 10,
  permissionsData,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleDeleteClick = (event) => {
    setSelectedEvent(event);
    setDeleteModalOpen(true);
  };

  const handleViewClick = (event) => {
    setSelectedEvent(event);
    setViewModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedEvent) return;

    try {
      await deleteEventRegistration(selectedEvent._id);
      toast.success("Registration deleted successfully");
      setDeleteModalOpen(false);
      setSelectedEvent(null);
      refetch?.();
    } catch (error) {
      toast.error("Failed to delete registration");
      console.error(error);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedEvent(null);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedEvent(null);
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm overflow-x-auto min-h-[500px]">
        <table className="min-w-full table-fixed text-sm border border-gray-300">
          <thead className="bg-[#4F22D8] text-white text-base">
            <tr>
              <th className="py-3 px-4 text-left font-semibold border-r border-gray-400 w-16">
                No
              </th>
              <th className="py-3 px-4 text-left font-semibold border-r border-gray-400 w-[180px]">
                Name
              </th>
              <th className="py-3 px-4 text-left font-semibold border-r border-gray-400 w-[140px]">
                Mobile
              </th>
              <th className="py-3 px-4 text-left font-semibold border-r border-gray-400 w-[200px]">
                Email
              </th>
              <th className="py-3 px-4 text-left font-semibold border-r border-gray-400 w-[150px]">
                Location
              </th>
              <th className="py-3 px-4 text-left font-semibold border-r border-gray-400 w-[180px]">
                Company
              </th>
              <th className="py-3 px-4 text-left font-semibold w-[150px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="text-center py-8 text-gray-500 text-base"
                >
                  No registrations found.
                </td>
              </tr>
            ) : (
              events.map((event, idx) => (
                <tr
                  key={event._id}
                  className="border-t border-gray-300 text-[14px] hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 text-center text-black border-r border-gray-300">
                    {currentIndex + idx + 1}
                  </td>
                  <td className="px-4 py-3 text-black border-r border-gray-300 font-medium">
                    {event.name || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-black border-r border-gray-300">
                    {event.mobile || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-black border-r border-gray-300 break-all">
                    {event.email || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-black border-r border-gray-300">
                    {event.location || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-black border-r border-gray-300">
                    {event.companyName || "N/A"}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleViewClick(event)}
                        className="text-purple-600 hover:text-purple-700 font-semibold text-sm flex items-center gap-1 transition-colors"
                        title="View Details"
                      >
                        <Eye size={14} />
                        View
                      </button>

                      <div className="w-px h-4 bg-gray-300" />
                      <button
                        onClick={() => handleDeleteClick(event)}
                        className="text-[#D40404] hover:text-red-700 font-semibold text-sm flex items-center gap-1 transition-colors"
                        title="Delete Registration"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Details Modal */}
      <ViewDetailsModal
        isOpen={viewModalOpen}
        onClose={handleCloseViewModal}
        eventData={selectedEvent}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onRequestClose={handleCloseDeleteModal}
        onDelete={handleConfirmDelete}
        itemName={selectedEvent?.name || "Unknown"}
        itemType="registration"
      />
    </>
  );
};
